// Thunks extracted from MyCalendarModule.js

import _ from "lodash";
import client from "lib/apolloClient/client";
import moment from "moment-timezone";
import update from "immutability-helper";
import {
  createPlatformCalendarEventMutation,
  deletePlatformCalendarEventMutation,
  editPlatformCalendarEventMutation,
  updatePlatformCalendarEventBasicDetailsMutation,
  createCalendarSyncAccessToken,
  updateAssignmentScheduleAndDueTime,
  createVideoMeetingMutation,
  editVideoMeetingMutation,
} from "../MyCalendarMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  TrimTrailingSlashes,
  isURL,
  formatDate,
  getGlobalTimezone,
} from "Utils";
import { generateDateTimeString, getDurationInMinutes } from "../Utils";

import { CREATE } from "Constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  calculateTimeDifferenceInMinutes,
  getDateMomentObject,
  getDateMomentObjectCustom,
  getDateTimeDifference,
  getISOString,
  formatTimeString,
} from "Utils/DateTimeHelpers";
import React from "react";
import {
  updateState,
  updateViewOnlyTagsState,
  updateFullscreenViewData,
} from "./MyCalendarActions";
import { INITIAL_CONTENT_TYPES } from "./MyCalendarConstants";
import {
  canUserEditEventForAll,
  canUserEditEventForProgram,
  getUserEventCreationAccessLevel,
} from "MyCalendar/modules/Utils";
import { isNewVideoMeetingUIFeatureEnabled } from "MyCalendar/components/EventModalV2/modules/Utils";
import {
  getOrganizationGradesQuery,
  getUserCoursesV2Query,
  getOrganizationCoursesQuery,
  getCalendarEventItemUsersPoolQuery,
} from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
  CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
} from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

// Thunks
export const createPlatformCalendarEvent = ({
  meetingObj,
  courses,
  dateForMonthlyRecurringOption,
  toggleFullScreenLoader,
  onClickDiscard,
}) => {
  return async dispatch => {
    // check for title not empty for meeting
    if (_.isEmpty(_.get(meetingObj, "label", ""))) {
      dispatch(setToastMsg("toastMsgs:please_enter_title_error"));
      return;
    }
    const meetingType = _.get(meetingObj, "type", "");
    const meetingURL = TrimTrailingSlashes(
      _.trim(_.get(meetingObj, "url", ""))
    );
    const scheduleTime = _.get(meetingObj, "scheduleTime");
    const scheduleEndTime = _.get(meetingObj, "scheduleEndTime");
    const isZoomAuthorized = _.get(meetingObj, "isZoomAuthorized");
    const isGoogleAuthorized = _.get(meetingObj, "isGoogleAuthorized");
    const microsoftAuthToken = _.get(meetingObj, "microsoftAuthToken", "");
    const meetingAuthToken = _.get(meetingObj, "authToken", "");

    const isRecurring = _.get(meetingObj, "isRecurring", false);

    let sendMeetingObj = {};

    const newCourseIds = _.map(courses, course => course.id);
    const newUserIds = _.reduce(
      courses,
      (result, obj) => {
        result = result.concat(_.map(obj.students, student => student.id));
        return result;
      },
      []
    );

    if (!newCourseIds.length) {
      dispatch(setToastMsg("toastMsgs:please_add_atleast_one_class_error"));
      return;
    }

    sendMeetingObj = update(sendMeetingObj, {
      label: {
        $set: _.get(meetingObj, "label", ""),
      },
    });

    sendMeetingObj = update(sendMeetingObj, {
      description: {
        $set: _.get(meetingObj, "description", ""),
      },
    });

    const scheduleString = generateDateTimeString({
      dateString: _.get(meetingObj, "scheduleDate"),
      timeString: _.get(meetingObj, "scheduleTime"),
    });

    const scheduleStringToSend = !_.isEmpty(scheduleString)
      ? moment(scheduleString, "YYYY-MM-DD:HH:mm", true).isValid()
        ? moment(scheduleString, "YYYY-MM-DD:HH:mm").toISOString()
        : moment(scheduleString, "YYYY-MM-DD").toISOString()
      : moment().toISOString();

    sendMeetingObj = update(sendMeetingObj, {
      startAt: {
        $set: scheduleStringToSend,
      },
    });

    const startTime = moment(scheduleTime, "HH:mm");
    const endTime = moment(scheduleEndTime, "HH:mm");
    // calculate total duration
    const duration = moment.duration(endTime.diff(startTime));

    const minutes = parseInt(duration.asMinutes());

    sendMeetingObj = update(sendMeetingObj, {
      durationInMin: {
        $set: minutes,
      },
    });

    if (isRecurring) {
      sendMeetingObj["recurrenceSetting"] = {
        frequency: _.get(meetingObj, "frequency"),
        weekDays: _.get(meetingObj, "recurringWeekDays"),
        interval: _.get(meetingObj, "recurringInterval"),
        endDate: _.get(meetingObj, "scheduledEndDate"),
        monthDays: _.get(meetingObj, "monthDays").length
          ? _.get(meetingObj, "monthDays")
          : _.get(meetingObj, "frequency") === "MONTHLY" &&
            _.get(meetingObj, "monthRecurringUnit") === "by_date"
          ? [+dateForMonthlyRecurringOption]
          : [],
        setPositions: _.get(meetingObj, "setPositions"),
        timezone: getGlobalTimezone(),
      };
    } else {
      sendMeetingObj["recurrenceSetting"] = null;
    }

    sendMeetingObj["meeting"] = {};

    sendMeetingObj = update(sendMeetingObj, {
      meeting: {
        type: {
          $set: meetingType,
        },
      },
    });

    switch (meetingType) {
      case "OTHER":
        // check for url not empty for OTHER meeting type
        if (_.isEmpty(meetingURL)) {
          dispatch(setToastMsg("toastMsgs:please_add_meeting_link"));
          return;
        } else if (!isURL(meetingURL)) {
          dispatch(setToastMsg("toastMsgs:please_enter_valid_link")); // if url is not valid display toast
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              url: {
                $set: _.startsWith(meetingURL, "https://")
                  ? meetingURL
                  : `https://${meetingURL}`,
              },
            },
          });
        }
        break;
      case "ZOOM":
        // check for whether user is zoom authorized or not to schedule a ZOOM call
        if (!isZoomAuthorized) {
          dispatch(setToastMsg("toastMsgs:please_link_zoom_account"));
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              authToken: {
                $set: meetingAuthToken,
              },
            },
          });
        }
        break;
      case "MICROSOFT":
        // check for whether microsoftAuthToken is there or not to schedule a MICROSOFT call
        if (_.isEmpty(microsoftAuthToken)) {
          dispatch(setToastMsg("toastMsgs:please_link_microsoft_account"));
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              authToken: {
                $set: microsoftAuthToken,
              },
            },
          });
        }
        break;
      case "GOOGLE":
        // check for whether microsoftAuthToken is there or not to schedule a MICROSOFT call
        if (!isGoogleAuthorized) {
          dispatch(setToastMsg("toastMsgs:please_link_google_account"));
          return;
        }
        break;
    }
    sendMeetingObj["courseIds"] = newCourseIds;
    sendMeetingObj["userIds"] = newUserIds;
    try {
      toggleFullScreenLoader(true);
      const result = await client.mutate({
        mutation: createPlatformCalendarEventMutation,
        variables: {
          ...sendMeetingObj,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      toggleFullScreenLoader(false);
      if (
        _.get(result, "data.platform.createPlatformCalendarEvent.warning") ||
        !_.get(result, "data.platform.createPlatformCalendarEvent.response")
      ) {
        if (
          _.get(
            result,
            "data.platform.createPlatformCalendarEvent.warning.errorCode"
          ) === "TD_MICROSOFT_TEAM_API_ERROR"
        ) {
          return "TD_MICROSOFT_TEAM_API_ERROR";
        }
        dispatch(
          setToastMsg(
            `toastMsgs:${_.toLower(
              _.get(
                result,
                "data.platform.createPlatformCalendarEvent.warning.message",
                "something_went_wrong"
              )
            )}`
          )
        );
        return false;
      }
      onClickDiscard();
      return true;
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    } finally {
      toggleFullScreenLoader(false);
    }
  };
};

export const deletePlatformCalendarEvent = ({
  id,
  actionFor,
  refetchEvents,
  refetchAssignedData,
  setShowRecurringDeleteOptions,
  onCloseEventModal,
  message = "",
  sendMessage = false,
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: deletePlatformCalendarEventMutation,
        variables: {
          id,
          actionFor,
          notificationMessage: message,
          shouldNotifyParticipants: sendMessage,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:event_deleted_successfully",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } finally {
      await refetchEvents();
      if (refetchAssignedData) {
        await refetchAssignedData();
      }
      setShowRecurringDeleteOptions(false);
      onCloseEventModal();
    }
  };
};

export const editPlatformCalendarEvent = ({
  meetingObj,
  courses,
  dateForMonthlyRecurringOption,
  oldCourseIds,
  oldUserIds,
  toggleFullScreenLoader,
  onClickDiscard,
  actionFor,
}) => {
  return async dispatch => {
    // check for title not empty for meeting
    if (_.isEmpty(_.get(meetingObj, "label", ""))) {
      dispatch(setToastMsg("toastMsgs:please_enter_title_error"));
      return;
    }
    const meetingType = _.get(meetingObj, "type", "");
    const meetingURL = TrimTrailingSlashes(
      _.trim(_.get(meetingObj, "url", ""))
    );
    const scheduleTime = _.get(meetingObj, "scheduleTime");
    const scheduleEndTime = _.get(meetingObj, "scheduleEndTime");
    const isZoomAuthorized = _.get(meetingObj, "isZoomAuthorized");
    const microsoftAuthToken = _.get(meetingObj, "microsoftAuthToken", "");
    const meetingAuthToken = _.get(meetingObj, "authToken", "");

    const isRecurring = _.get(meetingObj, "isRecurring", false);

    let sendMeetingObj = {};

    const newCourseIds = _.map(courses, course => course.id);
    const newUserIds = _.reduce(
      courses,
      (result, obj) => {
        result = result.concat(_.map(obj.students, student => student.id));
        return result;
      },
      []
    );

    if (!newCourseIds.length) {
      dispatch(setToastMsg("toastMsgs:please_add_atleast_one_class_error"));
      return;
    }

    sendMeetingObj = update(sendMeetingObj, {
      label: {
        $set: _.get(meetingObj, "label", ""),
      },
    });

    sendMeetingObj = update(sendMeetingObj, {
      description: {
        $set: _.get(meetingObj, "description", ""),
      },
    });

    const scheduleString = generateDateTimeString({
      dateString: _.get(meetingObj, "scheduleDate"),
      timeString: _.get(meetingObj, "scheduleTime"),
    });

    const scheduleStringToSend = !_.isEmpty(scheduleString)
      ? moment(scheduleString, "YYYY-MM-DD:HH:mm", true).isValid()
        ? moment(scheduleString, "YYYY-MM-DD:HH:mm").toISOString()
        : moment(scheduleString, "YYYY-MM-DD").toISOString()
      : moment().toISOString();

    sendMeetingObj = update(sendMeetingObj, {
      startAt: {
        $set: scheduleStringToSend,
      },
    });

    const startTime = moment(scheduleTime, "HH:mm");
    const endTime = moment(scheduleEndTime, "HH:mm");
    // calculate total duration
    const duration = moment.duration(endTime.diff(startTime));

    const minutes = parseInt(duration.asMinutes());

    sendMeetingObj = update(sendMeetingObj, {
      durationInMin: {
        $set: minutes,
      },
    });

    if (isRecurring) {
      sendMeetingObj["recurrenceSetting"] = {
        frequency: _.get(meetingObj, "frequency"),
        weekDays: _.get(meetingObj, "recurringWeekDays"),
        interval: _.get(meetingObj, "recurringInterval"),
        endDate: _.get(meetingObj, "scheduledEndDate"),
        monthDays: _.get(meetingObj, "monthDays").length
          ? _.get(meetingObj, "monthDays")
          : _.get(meetingObj, "frequency") === "MONTHLY" &&
            _.get(meetingObj, "monthRecurringUnit") === "by_date"
          ? [+dateForMonthlyRecurringOption]
          : [],
        setPositions: _.get(meetingObj, "setPositions"),
        timezone: getGlobalTimezone(),
      };
    } else {
      sendMeetingObj["recurrenceSetting"] = null;
    }

    sendMeetingObj["meeting"] = {};

    sendMeetingObj = update(sendMeetingObj, {
      meeting: {
        type: {
          $set: meetingType,
        },
      },
    });

    switch (meetingType) {
      case "OTHER":
        // check for url not empty for OTHER meeting type
        if (_.isEmpty(meetingURL)) {
          dispatch(setToastMsg("toastMsgs:please_add_meeting_link"));
          return;
        } else if (!isURL(meetingURL)) {
          dispatch(setToastMsg("toastMsgs:please_enter_valid_link")); // if url is not valid display toast
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              url: {
                $set: _.startsWith(meetingURL, "https://")
                  ? meetingURL
                  : `https://${meetingURL}`,
              },
            },
          });
        }
        break;
      case "ZOOM":
        // check for whether user is zoom authorized or not to schedule a ZOOM call
        if (!isZoomAuthorized) {
          dispatch(setToastMsg("toastMsgs:please_link_zoom_account"));
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              authToken: {
                $set: meetingAuthToken,
              },
            },
          });
        }
        break;
      case "MICROSOFT":
        // check for whether microsoftAuthToken is there or not to schedule a MICROSOFT call
        if (_.isEmpty(microsoftAuthToken)) {
          dispatch(setToastMsg("toastMsgs:please_link_microsoft_account"));
          return;
        } else {
          sendMeetingObj = update(sendMeetingObj, {
            meeting: {
              authToken: {
                $set: microsoftAuthToken,
              },
            },
          });
        }
        break;
    }
    sendMeetingObj.id = _.get(meetingObj, "id");
    sendMeetingObj.removedCourseIds = _.differenceWith(
      oldCourseIds,
      newCourseIds,
      _.isEqual
    );
    sendMeetingObj.addedCourseIds = _.differenceWith(
      newCourseIds,
      oldCourseIds,
      _.isEqual
    );
    sendMeetingObj.removedUserIds = _.differenceWith(
      oldUserIds,
      newUserIds,
      _.isEqual
    );
    sendMeetingObj.addedUserIds = _.differenceWith(
      newUserIds,
      oldUserIds,
      _.isEqual
    );
    try {
      toggleFullScreenLoader(true);
      const result = await client.mutate({
        mutation: editPlatformCalendarEventMutation,
        variables: {
          ...sendMeetingObj,
          actionFor,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      toggleFullScreenLoader(false);
      if (
        _.get(result, "data.platform.editPlatformCalendarEvent.warning") ||
        !_.get(result, "data.platform.editPlatformCalendarEvent.response")
      ) {
        dispatch(
          setToastMsg(
            `toastMsgs:${_.toLower(
              _.get(
                result,
                "data.platform.editPlatformCalendarEvent.warning.message",
                "something_went_wrong"
              )
            )}`
          )
        );
        return false;
      }
      onClickDiscard();
      return true;
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    } finally {
      toggleFullScreenLoader(false);
    }
  };
};

export const updatePlatformCalendarEventBasicDetails = ({
  id,
  rescheduleData,
  itemType,
  setIsQuickReschedule,
}) => {
  return async dispatch => {
    const startTime = moment(
      _.get(rescheduleData, "scheduleStartTime", ""),
      "hh:mm a"
    );
    const endTime = moment(
      _.get(rescheduleData, "scheduleEndTime", ""),
      "hh:mm a"
    );
    const duration = moment.duration(endTime.diff(startTime));
    const durationInMin = parseInt(duration.asMinutes());

    const scheduleString = generateDateTimeString({
      dateString: _.get(rescheduleData, "scheduleDate"),
      timeString: moment(_.get(rescheduleData, "scheduleStartTime", ""), [
        "hh:mm a",
      ]).format("HH:mm"),
    });
    const startAt = !_.isEmpty(scheduleString)
      ? moment(scheduleString, "YYYY-MM-DD:HH:mm", true).isValid()
        ? moment(scheduleString, "YYYY-MM-DD:HH:mm").toISOString()
        : moment(scheduleString, "YYYY-MM-DD").toISOString()
      : moment().toISOString();

    try {
      await client.mutate({
        mutation: updatePlatformCalendarEventBasicDetailsMutation,
        variables: {
          id,
          startAt,
          itemType,
          durationInMin,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      return true;
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    } finally {
      setIsQuickReschedule(false);
    }
  };
};

export const quickUpdateAssignmentScheduleAndDueTime = ({
  id,
  courseId,
  itemType,
  publishedAt,
  calendarAssignmentEndAt,
  dueDate,
  setIsQuickReschedule,
  closeSubmissionDate,
}) => {
  return async dispatch => {
    const startTime = getDateMomentObject(publishedAt);
    const endTime = getDateMomentObject(calendarAssignmentEndAt);

    const durationInMin = getDateTimeDifference({
      date1: endTime,
      date2: startTime,
      differenceBy: DATE_TIME_CONSTANTS.minutes,
    });

    try {
      await client.mutate({
        mutation: updatePlatformCalendarEventBasicDetailsMutation,
        variables: {
          id,
          startAt: publishedAt,
          itemType,
          durationInMin,
          eventType: "ASSIGNMENT",
        },
        fetchPolicy: "no-cache",
      });
      // Since the above mutation does not support updating deadline of assignment, using assignment specific mutation
      await client.mutate({
        mutation: updateAssignmentScheduleAndDueTime,
        variables: {
          id: _.get(_.split(id, "=>"), "1", null),
          courseId,
          deadline: dueDate,
          closeSubmissionDate: closeSubmissionDate,
        },
        fetchPolicy: "no-cache",
      });
      return true;
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    } finally {
      setIsQuickReschedule(false);
    }
  };
};

export const createSchoolCalendarEvent = ({
  start,
  end,
  entityIds,
  categoryId,
  title,
  description,
  location,
  author,
  action,
  isFullDayEvent = true,
  attachments,
  entityType = "GRADE",
  sharedWith,
  eventReminders,
  shouldNotifyParticipants = false,
  recurrenceSetting,
  metadata,
}) => {
  const startDate = isFullDayEvent
    ? formatDate({
        date: start,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(start).toISOString();
  const endDate = isFullDayEvent
    ? formatDate({
        date: end,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(end).toISOString();
  const startTime = moment(start, "hh:mm a");
  const endTime = moment(end, "hh:mm a");
  const duration = moment.duration(endTime.diff(startTime));
  const durationInMin = isFullDayEvent
    ? getDurationInMinutes({
        start,
        end,
      })
    : parseInt(duration.asMinutes());

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: createPlatformCalendarEventMutation,
        variables: {
          startDateTime: startDate,
          endDateTime: endDate,
          categoryId,
          label: title,
          description,
          location,
          entityIds,
          isFullDayEvent,
          entityType,
          eventType: "SCHOOL_CALENDAR_EVENT",
          attachments,
          sharedWith,
          eventReminders,
          durationInMin,
          shouldNotifyParticipants,
          recurrenceSetting,
          metadata,
        },
      });
      if (action === CREATE) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:event_action_successfully",
            type: "success",
            locale_params: [
              { key: "action", value: "toastMsgs:created", isPlainText: false },
            ],
          })
        );
      } else {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:event_action_successfully",
            type: "success",
            locale_params: [
              {
                key: "action",
                value: "toastMsgs:duplicated",
                isPlainText: false,
              },
            ],
          })
        );
      }
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

const getMeetingDurationInMinutes = (start, end) => {
  const startTime = getDateMomentObjectCustom(
    start,
    DATE_TIME_CONSTANTS.hh_mm_a
  );
  const endTime = getDateMomentObjectCustom(end, DATE_TIME_CONSTANTS.hh_mm_a);
  return calculateTimeDifferenceInMinutes({ startTime, endTime });
};

export const createVideoMeeting = ({
  start,
  end,
  entityIds,
  title,
  description,
  action,
  attachments,
  entityType = "GRADE",
  sharedWith,
  eventReminders,
  shouldNotifyParticipants = false,
  recurrenceSetting,
  metadata,
  meeting,
}) => {
  const startDate = getISOString(start);
  const endDate = getISOString(end);
  const durationInMin = getMeetingDurationInMinutes(start, end);

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: createVideoMeetingMutation,
        variables: {
          startDateTime: startDate,
          endDateTime: endDate,
          label: title,
          description,
          entityIds,
          entityType,
          eventType: "VIDEO_MEETING",
          attachments,
          sharedWith,
          eventReminders,
          durationInMin,
          shouldNotifyParticipants,
          recurrenceSetting,
          metadata,
          meeting,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      if (action === CREATE) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:video_meeting_created_successfully",
            type: "success",
            locale_params: [
              { key: "action", value: "toastMsgs:created", isPlainText: false },
            ],
          })
        );
      }
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const editVideoMeeting = ({
  id,
  start,
  end,
  entityIds,
  title,
  description,
  attachments,
  entityType = "GRADE",
  sharedWith,
  eventReminders,
  shouldNotifyParticipants = false,
  recurrenceSetting,
  metadata,
  meeting,
  actionFor = "ALL",
  initialAttachments,
  message = "",
}) => {
  const startDate = getISOString(start);
  const endDate = getISOString(end);
  const durationInMin = getMeetingDurationInMinutes(start, end);
  const addedAttachments = _.differenceBy(
    attachments,
    initialAttachments,
    "id"
  );
  const removedAttachments = _.map(
    _.differenceBy(initialAttachments, attachments, "id"),
    "id"
  );

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: editVideoMeetingMutation,
        variables: {
          id,
          startDateTime: startDate,
          endDateTime: endDate,
          label: title,
          description,
          entityType,
          eventType: "VIDEO_MEETING",
          sharedWith,
          eventReminders,
          durationInMin,
          shouldNotifyParticipants,
          recurrenceSetting,
          metadata,
          meeting,
          actionFor,
          addedEntityIds: entityIds,
          addedAttachments,
          removedAttachments,
          notificationMessage: message,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:event_action_successfully",
          type: "success",
          locale_params: [
            { key: "action", value: "toastMsgs:updated", isPlainText: false },
          ],
        })
      );
      return true;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return false;
    }
  };
};

export const editSchoolCalendarEvent = ({
  id,
  start,
  end,
  entityIds,
  categoryId,
  title,
  description,
  location,
  isFullDayEvent = true,
  attachments,
  initialAttachments,
  sharedWith,
  entityType = "GRADE",
  eventReminders,
  message,
  shouldNotifyParticipants = false,
  recurrenceSetting,
  metadata,
  actionFor = "ALL",
}) => {
  const startDate = isFullDayEvent
    ? formatDate({
        date: start,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(start).toISOString();
  const endDate = isFullDayEvent
    ? formatDate({
        date: end,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(end).toISOString();
  const addedAttachments = _.differenceBy(
    attachments,
    initialAttachments,
    "id"
  );
  const removedAttachments = _.map(
    _.differenceBy(initialAttachments, attachments, "id"),
    "id"
  );
  const startTime = moment(start, "hh:mm a");
  const endTime = moment(end, "hh:mm a");
  const duration = moment.duration(endTime.diff(startTime));
  const durationInMin = isFullDayEvent
    ? getDurationInMinutes({
        start,
        end,
      })
    : parseInt(duration.asMinutes());

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: editPlatformCalendarEventMutation,
        variables: {
          id,
          startDateTime: startDate,
          endDateTime: endDate,
          categoryId,
          label: title,
          description,
          location,
          actionFor,
          eventType: "SCHOOL_CALENDAR_EVENT",
          isFullDayEvent,
          entityType,
          addedEntityIds: entityIds,
          addedAttachments,
          removedAttachments,
          sharedWith,
          eventReminders,
          notificationMessage: message,
          durationInMin,
          shouldNotifyParticipants,
          recurrenceSetting,
          metadata,
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:event_action_successfully",
          type: "success",
          locale_params: [
            { key: "action", value: "toastMsgs:updated", isPlainText: false },
          ],
        })
      );
      return true;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return false;
    }
  };
};

export const editSchoolCalendarEventDates = ({
  id,
  start,
  end,
  isFullDayEvent = true,
  message = "",
  shouldNotifyParticipants = false,
}) => {
  const startDate = isFullDayEvent
    ? formatDate({
        date: start,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(start).toISOString();
  const endDate = isFullDayEvent
    ? formatDate({
        date: end,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      })
    : moment(end).toISOString();
  const startTime = moment(start);
  const endTime = moment(end);
  const duration = moment.duration(endTime.diff(startTime));
  const durationInMin = isFullDayEvent
    ? getDurationInMinutes({
        start: startDate,
        end: endDate,
      })
    : parseInt(duration.asMinutes());

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: updatePlatformCalendarEventBasicDetailsMutation,
        variables: {
          id,
          startDateTime: startDate,
          endDateTime: endDate,
          itemType: "CALENDAR_EVENT_ITEM",
          eventType: "SCHOOL_CALENDAR_EVENT",
          isFullDayEvent,
          durationInMin,
          notificationMessage: message,
          shouldNotifyParticipants,
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:event_action_successfully",
          type: "success",
          locale_params: [
            { key: "action", value: "toastMsgs:updated", isPlainText: false },
          ],
        })
      );
      return true;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return false;
    }
  };
};

export const deleteSchoolCalendarEvent = ({
  id,
  isFullDayEvent = true,
  message,
  sendMessage,
  actionFor = "ALL",
}) => {
  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: deletePlatformCalendarEventMutation,
        variables: {
          id,
          actionFor,
          eventType: "SCHOOL_CALENDAR_EVENT",
          isFullDayEvent,
          notificationMessage: message,
          shouldNotifyParticipants: sendMessage,
          shouldUseNewMeetingInfra: isNewVideoMeetingUIFeatureEnabled(),
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:event_deleted_successfully",
          type: "success",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const resetViewOnlyTagsState = () => {
  return dispatch => {
    dispatch(
      updateViewOnlyTagsState({
        schoolCalendarTagIds: [],
        assessmentTypes: [],
        dpTags: [],
        mypTags: [],
      })
    );
  };
};

export const resetCalendarState = () => {
  return dispatch => {
    dispatch(
      updateState({
        projectGroupId: null,
        courseId: null,
        projectGroups: [],
        courseProjectGroups: [],
        contentTypes: INITIAL_CONTENT_TYPES,
        selectedViewOnlyTags: [],
        viewOnlyTagsList: [],
        deadlinesCount: -1,
      })
    );
  };
};

export const resetFullScreenViewData = () => {
  return dispatch => {
    dispatch(
      updateFullscreenViewData({ calendarIds: [], notUserCourseIds: [] })
    );
  };
};

export const resetCalendarData = () => {
  return dispatch => {
    dispatch(resetCalendarState());
    dispatch(resetViewOnlyTagsState());
    dispatch(resetFullScreenViewData());
  };
};

export const getCalendarSyncAccessToken = () => {
  return async dispatch => {
    try {
      const calendarSyncData = await client.mutate({
        mutation: createCalendarSyncAccessToken,
        variables: {
          input: {
            serviceType: "CALENDAR",
          },
        },
      });
      return calendarSyncData;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return false;
    }
  };
};

const queryCalledOnceMap = new Map();
const getNetworkPolicy = entityType => {
  if (queryCalledOnceMap.has(entityType)) {
    return "cache-first";
  }

  queryCalledOnceMap.set(entityType, true);
  return "network-only";
};

export const isCalendarEventWithinUserScope = async ({
  userId,
  eventData,
  organizationId,
  currentCurriculumProgramId,
  academicYearId,
  organizationCurriculumPrograms,
}) => {
  const associatedEntities = _.get(eventData, "item.associatedEntities", []);
  const entityType =
    _.get(_.first(associatedEntities), "__typename") || "CUSTOM";
  const eventType = _.get(eventData, "item.eventType");
  let userEntityIds = [];
  const canPublishToProgram = canUserEditEventForProgram({
    currentCurriculumProgramId,
  });
  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId,
    alsoCheckClassLevelPermission: false,
  });

  const userCanCreateHolidaysAndWholeSchoolEvents = _.includes(
    ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
    userEventCreationAccessLevel
  );

  const userHasAccoutLevelAccess = _.includes(
    ["ACCOUNT_LEVEL"],
    userEventCreationAccessLevel
  );

  const filters = { curriculumProgramIds: [currentCurriculumProgramId] };

  const canPublishToAll = canUserEditEventForAll({
    currentCurriculumProgramId,
  });

  const programsUserCanPublishTo = _.filter(
    organizationCurriculumPrograms,
    ({ id }) =>
      ACLStorage.getPermissionValueByContext(
        CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
        [
          {
            entityId: id,
            entityType: "CURRICULUM_PROGRAM",
          },
        ]
      )
  );

  const fetchPolicy = getNetworkPolicy(entityType);

  try {
    switch (entityType) {
      case "Grade": {
        // get organization's grade for the selected curriculums
        const response = await client.query({
          query: getOrganizationGradesQuery,
          variables: {
            organizationId,
            gradeFilters: canPublishToAll
              ? undefined
              : {
                  curriculumProgramIds: _.map(
                    programsUserCanPublishTo,
                    ({ id }) => id
                  ),
                },
            getCurriculumProgram: false,
          },
          fetchPolicy,
        });
        userEntityIds = _.map(_.get(response, "data.node.grades", []), "id");
        break;
      }
      case "Course": {
        const isRnpConsumptionAllowed = ACLStore.can(
          ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION
        );
        if (!userCanCreateHolidaysAndWholeSchoolEvents) {
          const response = await client.query({
            query: getUserCoursesV2Query,
            variables: {
              filters: {
                fetchOnlyMappedCourses: false,
                academicYears: [academicYearId],
                ...(isRnpConsumptionAllowed
                  ? {
                      controlParameters: [
                        ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
                      ],
                    }
                  : {}),
              },
              id: userId,
              getGrades: true,
              getCurriculumProgram: false,
              userType: "STAFF",
            },
            fetchPolicy,
          });
          userEntityIds = _.map(
            _.get(response, "data.node.coursesV2.edges", []),
            "node.id"
          );
        } else {
          // get organization courses for the selected curriculums
          const response = await client.query({
            query: getOrganizationCoursesQuery,
            variables: {
              organizationId,
              courseFilters: {
                ...filters,
                academicYears: [academicYearId],
                ...(!userHasAccoutLevelAccess
                  ? {
                      curriculumProgramIds: _.map(
                        programsUserCanPublishTo,
                        ({ id }) => id
                      ),
                    }
                  : {}),
              },
              getCurriculumProgram: false,
              getGrades: true,
            },
            fetchPolicy,
          });
          userEntityIds = _.map(
            _.get(response, "data.node.courses.edges", []),
            "node.id"
          );
        }
        break;
      }
      case "CUSTOM": {
        const response = await client.query({
          query: getCalendarEventItemUsersPoolQuery,
          variables: {
            organizationId,
            getStudentFamilyMembers: true,
            getOnlyUserId: false,
            filters: {
              userStatus: "ACTIVE",
              academicYearIds: academicYearId ? [academicYearId] : undefined,
              curriculumProgramIds: undefined,
              ...(eventType ? { eventType } : {}),
            },
          },
          fetchPolicy,
        });
        userEntityIds = _.map(
          _.get(response, "data.node.calendarEventItemUsers.edges", []),
          "node.id"
        );
        // add the user's own id
        userEntityIds.push(userId);
        break;
      }
      default:
      // would never fall here
    }

    const userEntityIdsSet = new Set(userEntityIds);
    const entities =
      entityType === "CUSTOM"
        ? _.get(eventData, "item.users")
        : associatedEntities;

    const entityIds = _.map(entities, entity => _.get(entity, "id", ""));

    // entities should be subset of user's entities
    return _.every(entityIds, id => userEntityIdsSet.has(id));
  } catch (e) {
    return false;
  }
};

/**
 * Updates a revamped video meeting event in the calendar with new scheduling and configuration details.
 * Handles both single and recurring events with support for rescheduling, participant notifications, and recurrence settings.
 *
 * @async
 * @function quickUpdateRevampedVideoMeeting
 * @param {Object} params - The parameters object
 * @param {Object} params.repeatEventSettings - Settings for recurring events including weekDays and endDate
 * @param {string|moment.Moment} params.startTime - Original start time of the event
 * @param {string|moment.Moment} params.recurrenceStart - Start time for recurring events
 * @param {Object} params.rescheduleData - New scheduling information
 * @param {string} params.rescheduleData.scheduleDate - New date for the meeting in YYYY-MM-DD format
 * @param {string|moment.Moment} params.rescheduleData.scheduleStartTime - New start time for the meeting
 * @param {string|moment.Moment} params.rescheduleData.scheduleEndTime - New end time for the meeting
 * @param {string} params.id - Unique identifier of the calendar event
 * @param {Object} params.meeting - Meeting configuration object
 * @param {string} params.meeting.type - Type of meeting (e.g., "OTHER")
 * @param {string} [params.meeting.id] - Meeting ID for non-OTHER types
 * @param {Object} [params.meeting.urls] - URLs object containing meetingURL
 * @param {string} [params.meeting.urls.meetingURL] - URL for OTHER type meetings
 * @param {string} params.title - Title of the meeting event
 * @param {string} params.description - Description of the meeting event
 * @param {string} [params.message=""] - Optional message to send to participants
 * @param {boolean} [params.sendMessage=false] - Whether to notify participants about the update
 * @param {Object} params.eventDetails - Details about the event including participants
 * @param {Array} [params.eventDetails.eventAssociatedEntities=[]] - Entities associated with the event
 * @param {Array} [params.eventDetails.users=[]] - Users associated with the event
 * @param {Array} [params.eventReminders=[]] - Array of reminder objects for the event
 * @param {Array} [params.sharedWith=[]] - Array of users/entities the event is shared with
 * @param {string} [params.participantListType="GRADE"] - Type of participant list (e.g., "GRADE")
 * @param {Object} [params.metadata={}] - Additional metadata for the event
 * @param {string} params.recurringOption - Option for handling recurring events (e.g., "ALL", "SINGLE", "FOLLOWING")
 * @param {Function} params._refetchEvents - Callback function to refresh the events list
 * @param {Function} [params.refetchAssignedData] - Optional callback to refresh assigned data
 * @param {Function} params.setShowRecurringDeleteOptions - Callback to control recurring delete options visibility
 * @param {Function} params.onCloseEventModal - Callback to close the event modal on success
 * @returns {Promise<void>}
 */
export const quickUpdateRevampedVideoMeeting = async ({
  repeatEventSettings,
  startTime,
  recurrenceStart,
  rescheduleData,
  id,
  meeting,
  title,
  description,
  message = "",
  sendMessage = false,
  eventDetails,
  eventReminders = [],
  sharedWith = [],
  participantListType = "GRADE",
  metadata = {},
  recurringOption,
  _refetchEvents,
  refetchAssignedData,
  setShowRecurringDeleteOptions,
  onCloseEventModal,
  editVideoMeeting,
}) => {
  const weekDays =
    _.size(repeatEventSettings?.weekDays) === 1
      ? [
          getDateMomentObject(
            !_.isEmpty(repeatEventSettings) ? startTime : recurrenceStart
          )?.day(),
        ]
      : repeatEventSettings?.weekDays;

  const repeatEventEndDate = getDateMomentObject(repeatEventSettings?.endDate);
  const recurrenceSetting = !_.isEmpty(repeatEventSettings)
    ? {
        ...repeatEventSettings,
        weekDays,
        endDate: repeatEventEndDate?.isValid()
          ? formatDate({
              date: repeatEventEndDate,
              format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
            })
          : null,
      }
    : null;

  const updatedMeetingObject =
    meeting?.type === "OTHER"
      ? {
          url: meeting?.urls?.url || "",
          type: "OTHER",
        }
      : {
          id: meeting?.id,
        };

  const { scheduleDate, scheduleStartTime, scheduleEndTime } = rescheduleData;

  /**
   * Normalizes time input to 24-hour format (HH:mm)
   * Handles three input formats:
   * 1. Moment object - directly formats to HH:mm
   * 2. 12-hour format string with AM/PM (e.g., "05:30 pm") - converts to 24-hour format
   * 3. 24-hour format string (e.g., "17:30") - returns as is
   *
   * @param {string|moment.Moment} timeInput - The time to normalize
   * @returns {string} Time in HH:mm format (e.g., "17:30")
   */
  const getNormalizedTime = timeInput => {
    // Return fallback for empty/null/undefined input
    if (!timeInput) {
      return "00:00";
    }

    // Handle Moment objects - extract time in 24-hour format
    const momentObj = getDateMomentObject(timeInput);
    if (momentObj.isValid()) {
      return formatDate({ date: timeInput, format: "HH:mm" });
    }

    // Handle string inputs - try parsing as 12-hour format first (e.g., "05:30 pm")
    // Using strict parsing (true parameter) to ensure accurate validation
    const time12h = moment(timeInput, "hh:mm a", true);
    if (time12h.isValid()) {
      return formatTimeString({
        time: timeInput,
        preFormat: "hh:mm a",
        postFormat: "HH:mm",
      });
    }

    // Fallback to 24-hour format parsing (e.g., "17:30")
    const time24h = moment(timeInput, "HH:mm", true);
    if (time24h.isValid()) {
      return formatTimeString({
        time: timeInput,
        preFormat: "HH:mm",
        postFormat: "HH:mm",
      });
    }

    // If all parsing attempts fail, return fallback
    return "00:00";
  };

  const startTimeStr = getNormalizedTime(scheduleStartTime);
  const endTimeStr = getNormalizedTime(scheduleEndTime);

  const rescheduleStart = getDateMomentObjectCustom(
    `${scheduleDate} ${startTimeStr}`,
    "YYYY-MM-DD HH:mm"
  );
  const rescheduleEnd = getDateMomentObjectCustom(
    `${scheduleDate} ${endTimeStr}`,
    "YYYY-MM-DD HH:mm"
  );

  const { eventAssociatedEntities = [], users = [] } = eventDetails || {};

  const isSuccess = await editVideoMeeting({
    id,
    start: rescheduleStart,
    end: rescheduleEnd,
    title,
    description,
    entityIds: [..._.map(eventAssociatedEntities, "id"), ..._.map(users, "id")],
    entityType: participantListType,
    sharedWith,
    eventReminders: _.map(eventReminders, ({ __typename, ...rest }) => rest),
    recurrenceSetting: !_.isEmpty(repeatEventSettings)
      ? _.omit(recurrenceSetting, ["id", "endType", "__typename"])
      : null,
    metadata: !_.isEmpty(repeatEventSettings)
      ? {
          skipNonOperationalDays: false,
          ..._.omit(metadata, "__typename"),
        }
      : null,
    meeting: updatedMeetingObject,
    actionFor: recurringOption || "ALL",
    shouldNotifyParticipants: sendMessage,
    message,

    // The editVideoMeeting function calculates added and removed attachments internally, and since in quick edit we cannot change attachments,
    // we pass the same attachments as both current and initial to indicate no change. Even when the event has attachments.
    // attachments,
    // initialAttachments,
  });

  if (isSuccess) {
    _refetchEvents();
    if (refetchAssignedData) {
      refetchAssignedData();
    }
    setShowRecurringDeleteOptions(false);
    onCloseEventModal();
  }
};
